
import { ComponentBase } from '../components/ComponentBase';
import { template, unsafeHTML } from '../utils/TemplateParser';
import { Controls } from '../components/Controls';
import { lang } from '../lang';
import { vars } from '../utils/vars';
import { api } from '../utils/api';
import { formatStartDate, formatLinks, formatLocation } from '../utils';

export class EventDetails extends ComponentBase {
    constructor({id, instanceId}) {
        super({id: 'event-detail-page'});
        this.controls = new Controls({
            viewSelector: false,
            action: 'link'
        });
        
        this.eventId = id;
        this.instanceId = instanceId;
        this.fetch(id, instanceId);
    }

    fetch(id, instanceId) {
        Promise.all([
            api.getEvent({id, instanceId}),
            api.getTags()
        ])
        .then(data => {
            this.model = data[0];
            this.controls.tagCollection = data[1];
            this.controls.render();
            this.render();
        })
        .catch((error) => {
            // redirect to calendar homepage
            this.triggerRouteChange(this.getRouteURL(''));
        } );
    }

    registerEvents() {
        return {
            'click .copy-virtual-event-link' : 'copyVirtualEventLink',
            'click .tag' : 'handleTagClick',
            'click  #event-ical-download' : 'handleICalClick',
        }
    }

    /* Event Handlers */
    handleICalClick(e) {
        if(e.ctrlKey || e.metaKey) return true; // when ctrl or command keys are pressed during click trigger default behavior (open in new tab)
        e.preventDefault();
        api.getEventICS({ id: this.eventId });
    }

    handleTagClick(e) {
        // Link to list/grid view with filters
        const tagText = e.target.innerHTML;
        this.controls.el.querySelector(`.filter-by-tag [data-value="${tagText}"]`).click();
    }

    copyVirtualEventLink() {
        navigator.clipboard.writeText(this.model.conferenceLink)
        .then(() => {
            this.elements.copyText.innerText = lang.CopiedExclamation;
            startTimer();
        })
        .catch(() => {
            this.elements.copyText.innerText = lang.CopyError;
            startTimer();
        });

        const startTimer = () => {
            setTimeout(() => {
                this.elements.copyText.innerText = lang.Copy;
            }, 4000);
        };
    }

    template() {
        return template`
            <div class="hero">
                <div class="wrapper">
                    ${this.controls}
                </div>
            </div>
            <div class="wrapper">
                <div role="region" class="page" aria-labelledby="event-title">
                    <div class="event-detail" data-calendar-id="${this.model.category}" data-calendar-name="${this.model.categoryName}" data-event-id="${this.model.id}" data-event-title="${this.model.title}">
                        ${this.sidebarTemplate()}
                        ${this.contentTemplate()}
                    </div>
                </div>
            </div>
            
        `;
    }

    sidebarTemplate() {
        return template`
            <section class="sidebar">
                <div class="sidebar-background">
                    ${this.imageTemplate()}
                    <div class="details">
                        <div class="detail-with-icon date" tabindex="0" aria-label="${lang.EventDate}"><i class="icon fas fa-calendar-alt"></i> <div>${formatStartDate(this.model)} <br> <a href="${api.getEventICS({ id: this.eventId }, false)}" id="event-ical-download">${lang.AddToMyCalendar}</a></div></div>
                        ${this.organizerTemplate()}
                        ${this.locationTemplate()}
                        ${this.controls.ticketTemplate(this.model)}
                    </div>
                    ${this.virtualEventTemplate()}
                    ${this.mapTemplate()}
                </div>
            </section>
        `;

        // This version contains elements meant for phase 2 and beyond. Add these elements as needed in the future
        return template`
            <section class="sidebar">
                ${this.imageTemplate()}
                <div class="details">
                    <div class="detail-with-icon date" aria-label="${lang.EventDate}"><i class="icon fas fa-calendar-alt"></i> <div>${formatStartDate(this.model)} <br> <a href="">${lang.AddToMyCalendar}</a></div></div>
                    ${this.locationTemplate()}
                    <div class="detail-with-icon email"><i class="icon fas fa-envelope-open-text"></i> <div><a href="">Email Sponsor</a></div></div>
                    <div class="detail-with-icon open-to"><i class="icon fas fa-users-class"></i> <div><div class="bold">This event is open to:</div> Faculty/Staff, Students, Alumni/Friends</div></div>
                    <div class="detail-with-icon tickets" aria-label="Tickets"><i class="icon fas fa-ticket-alt"></i> <div><div class="bold">Free: 457 spots remaining</div> <a href="">Register today!</a></div></div>
                </div>
                ${this.virtualEventTemplate()}
                ${this.mapTemplate()}
            </section>
        `;
    }

    organizerTemplate() {
        const image = this.model.organizerDetail?.image ?? '';
        const imageAltText = this.model.organizerDetail?.imageAltText ?? '';
        let imageTemplate = '';
        if(image){
            imageTemplate = template`<img src="${image}" alt="${imageAltText}">`;
        }
        return this.model.organizer ? template`<div class="detail-with-icon organizer" tabindex="0" aria-label="${lang.EventOrganizer}"><i class="icon fas fa-users-class"></i> <div>${this.model.organizer}</div><div class="${image ? '' : 'hide'}">${imageTemplate}</div></div>` : '';
    }
    
    locationTemplate() {
        if(this.model.locationOther) return this.locationOtherTemplate(); // This simplified location template is used whenever the locationOther property exists.
        const showMap = this.model.locationDetail?.showMap ?? false;
        const address = this.model.locationDetail?.address ?? '';
        const image = this.model.locationDetail?.image ?? '';
        const imageAltText = this.model.locationDetail?.imageAltText ?? '';
        let imageTemplate = '';
        if(image){
            imageTemplate = template`<img src="${image}" alt="${imageAltText}">`;
        }
        return this.model.location || this.model.locationRoom ? template`<div class="detail-with-icon location" tabindex="0" aria-label="${lang.EventLocation}"><i class="icon fas fa-map-marker-alt"></i> <div>${this.model.location ?? ''} ${this.model.locationRoom ?? ''}<br>${showMap ? '' : address}</div><div class="${image ? '' : 'hide'}">${imageTemplate}</div></div>` : '';
    }

    locationOtherTemplate() {
        const location = formatLocation(this.model.locationOther, true);
        return template`<div class="detail-with-icon location-other" title="${this.model.locationOther}" tabindex="0" aria-label="${lang.EventLocation}"><i class="icon fas fa-map-marker-alt"></i> <div>${unsafeHTML(location)}</div></div>`;
    }

    virtualEventTemplate() {
        if (!this.model.conferenceLink) {
            return '';
        }

        const joinText = this.model.conferenceButtonLabel || lang.JoinOnline;
        return template`
            <div class="details virtual-event-link-wrapper" tabindex="0">
                <div class="detail-with-icon virtual-event-link" aria-label="${lang.VirtualEvent}>
                    <i class="icon fas fa-laptop-house"></i>
                    <div>
                        <div class="bold"><a href="${this.model.conferenceLink}" target="_blank">${joinText}</a></div>
                        <button class="detail-with-icon copy-virtual-event-link" aria-live="polite" aria-label="${lang.CopyVirtualEventLink}"><i class="icon fas fa-link"></i> <div>${lang.Copy}</div></button>
                    </div>
                </div>
            </div>
        `;
    }

    mapTemplate() {
        if(!this.model.location || !vars.pubcalendar.googleMapKey?.length || this.model.locationDetail.showMap === false) return '';
        return this.getEmbedMapElement();
        // return this.getStaticMapElement(); // In case we decide to add an option in appearance settings to use the static image map instead since it's about half the cost per request than the embed API
    }

    getEmbedMapElement() {
        const apiKey = vars.pubcalendar.googleMapKey;
        const location = this.model.locationDetail.address;
        const mapIframe = document.createElement('iframe');
        mapIframe.id = 'google_maps_iframe';
        mapIframe.src = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(location)}&maptype=roadmap&zoom=15`;
        mapIframe.width = '352';
        mapIframe.height = '352';
        mapIframe.style = 'border:0';
        mapIframe.loading = 'lazy';
        mapIframe.allowFullscreen = true;
        return mapIframe;
    }

    /*
    getStaticMapElement() {
        const apiKey = vars.pubcalendar.googleMapKey;
        const location = this.model.locationDetail.address;
        const mapLink = document.createElement('a');
        const mapImage = document.createElement('img');
        mapLink.href = `https://www.google.com/maps/place/${encodeURIComponent(location)}`;
        mapLink.target = '_blank';
        mapImage.src = `https://maps.googleapis.com/maps/api/staticmap?markers=${encodeURIComponent(location)}&size=352x352&maptype=roadmap&key=${apiKey}`;
        mapLink.appendChild(mapImage);
        return mapLink;
    }
    */

    contentTemplate() {
        const organizer = this.model.organizer ? `${lang.OrganizedBy} ${this.model.organizer}` : '';
        return template`
            <section class="main-content-area">
                ${this.eventLabelTemplate()}
                <h2 class="event-title" id="event-title" tabindex="0">${this.model.title}</h2>
                <div class="sponsored-by" ${organizer ? `tabindex="0"` : ''}>${organizer}</div>
                
                ${this.imageTemplate()}
                ${this.descriptionTemplate()}
                ${this.tagsTemplate()}
            </section>
        `;
    }

    eventLabelTemplate(event) {
        let status;
        const classes = ['event-label'];
        
        switch(this.model.status) {
            case "POSTPONED":
                status = lang.Postponed;
                classes.push('is-postponed');
                break;
            
            case "CANCELLED":
                status = lang.Cancelled;
                classes.push('is-cancelled');
                break;

            default:
                status = this.model.categoryName;
        }

        return template`<div class="${classes.join(' ')}" tabindex="0">${status}</div>`;
    }

    imageTemplate() {
        if (!this.model.image) { return ''; }

        return template`<img class="event-thumb" src="${this.model.image}" alt="${this.model.imageAltText}">`;
    }

    descriptionTemplate() {
        if (!this.model.description) { return ''; }

        return template`
            <div class="event-description" tabindex="0">
                ${unsafeHTML(formatLinks(this.model.description))}
            </div>
        `;
    }

    tagsTemplate() {
        if (!this.model.tags.length) { return ''; }

        let content = `${lang.Related} ` + this.model.tags.map(tag => {
            return `<a class="tag" href="javascript:;" role="button" tabindex="0" aria-label="${lang.Related}: ${tag}">${tag}</a>`;
        }).join('');

        return template`<div class="related" tabindex="0" >
            ${unsafeHTML(content)}
        </div>`;
    }

    afterRender() {
        this.elements = {
            copyText: this.el.querySelector('.copy-virtual-event-link div')
        };
        api.setPageTitle(this.model.title);
        api.setPageDescription(this.model.description);
        api.setPageCanonical(document.location.href);
        api.adjustScrollPosition();
        this.personalizationTracking(this.model.id);
    }

    personalizationTracking(eventID) {
        if(!window._paq) return; // if _paq global variable does not exist it means tracking hasn't been enabled for this calendar
        window._paq.push(['setCustomUrl', `${window.location.pathname}/CALENDAR_EVENT/${eventID}`]);
        window._paq.push(['trackPageView']);
    }
}